import { useEffect, type RefObject } from 'react';

/**
 * Calls `onOutsideClick` on a `mousedown` outside of `ref`'s element —
 * the ref+mousedown pattern used across every dropdown/popup in this app
 * (DropdownFilter, LogsDateRangeFilter, LogsFilterBar's calendar popups).
 *
 * `enabled` lets callers skip attaching the listener entirely while the
 * popup is already closed, rather than attaching it unconditionally and
 * checking an `isOpen` flag inside the handler.
 */
export function useOutsideClick(
  ref: RefObject<HTMLElement | null>,
  onOutsideClick: () => void,
  enabled = true
): void {
  useEffect(() => {
    if (!enabled) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onOutsideClick();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, onOutsideClick, enabled]);
}

export default useOutsideClick;
